import { useEffect, useRef, useState } from "react";

// The stream being clipped, playing on the clipping screen with OUR controls:
// Twitch's / YouTube's own controls, titles and logos are hidden, a tap layer
// stops taps from opening twitch.tv / youtube.com, and a Clipbait timeline lets
// them scrub. Uses each platform's official player API (Twitch.Player /
// YT.Player). Kick has no player API, so it gets the plain embed.

const loaded = {};
function loadScript(src) {
  if (!loaded[src]) {
    loaded[src] = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return loaded[src];
}
let ytReady = null;
function loadYouTube() {
  if (window.YT?.Player) return Promise.resolve();
  if (!ytReady) {
    ytReady = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(); };
    });
    loadScript("https://www.youtube.com/iframe_api").catch(() => {});
  }
  return ytReady;
}

export function sourceFor(url) {
  if (!url) return null;
  let m;
  if ((m = /twitch\.tv\/videos\/(\d+)/i.exec(url))) return { kind: "twitch", video: m[1] };
  if ((m = /twitch\.tv\/([a-z0-9_]{3,25})\/?(?:$|\?)/i.exec(url))) return { kind: "twitch", channel: m[1] };
  if ((m = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|live\/|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/i.exec(url))) return { kind: "youtube", id: m[1] };
  if ((m = /kick\.com\/([a-z0-9_-]{2,30})\/?(?:$|\?)/i.exec(url))) return { kind: "kick", channel: m[1] };
  return null;
}

const fmt = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};

export default function VodPlayer({ source, onFail }) {
  const host = useRef(null);
  const player = useRef(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [paused, setPaused] = useState(true);
  const [muted, setMuted] = useState(true);
  const [scrub, setScrub] = useState(null); // value while dragging
  // They pressed pause. The embeds resume on their own after a pause (Twitch
  // starts again once it has buffered more), so a play we didn't ask for is
  // paused straight back until they press play.
  const userPaused = useRef(false);

  useEffect(() => {
    if (!source || source.kind === "kick" || !host.current) return undefined;
    let cancelled = false;
    let tick;
    const el = host.current;
    (async () => {
      try {
        if (source.kind === "twitch") {
          await loadScript("https://player.twitch.tv/js/embed/v1.js");
          if (cancelled) return;
          const p = new window.Twitch.Player(el, {
            width: "100%", height: "100%", autoplay: true, muted: true, controls: false,
            parent: [window.location.hostname],
            ...(source.video ? { video: `v${source.video}` } : { channel: source.channel }),
          });
          // Playing / paused comes from the player's own events: no guessing.
          const T = window.Twitch.Player;
          p.addEventListener(T.PLAYING, () => {
            if (userPaused.current) { p.pause(); return; }
            setPaused(false);
          });
          p.addEventListener(T.PAUSE, () => setPaused(true));
          p.addEventListener(T.ENDED, () => setPaused(true));
          player.current = {
            time: () => p.getCurrentTime(), duration: () => p.getDuration(), paused: () => p.isPaused(),
            seek: (t) => p.seek(t), play: () => p.play(), pause: () => p.pause(),
            muted: () => p.getMuted(), setMuted: (m) => p.setMuted(m),
          };
        } else {
          await loadYouTube();
          if (cancelled) return;
          const mount = document.createElement("div");
          el.appendChild(mount);
          const p = new window.YT.Player(mount, {
            width: "100%", height: "100%", videoId: source.id,
            playerVars: { autoplay: 1, mute: 1, controls: 0, playsinline: 1, rel: 0, modestbranding: 1, disablekb: 1, fs: 0, iv_load_policy: 3 },
            events: {
              // 1 playing · 2 paused · 0 ended (3 = buffering: leave as is)
              onStateChange: (e) => {
                if (e.data === 1) { if (userPaused.current) p.pauseVideo(); else setPaused(false); }
                else if (e.data === 2 || e.data === 0) setPaused(true);
              },
            },
          });
          player.current = {
            time: () => p.getCurrentTime?.() || 0, duration: () => p.getDuration?.() || 0,
            paused: () => p.getPlayerState?.() !== 1,
            seek: (t) => p.seekTo?.(t, true), play: () => p.playVideo?.(), pause: () => p.pauseVideo?.(),
            muted: () => !!p.isMuted?.(), setMuted: (m) => (m ? p.mute?.() : p.unMute?.()),
          };
        }
        // "Playing" = the clock is actually moving. When iPhones block autoplay
        // the API can still say it's playing, so trust the clock instead.
        tick = setInterval(() => {
          const pl = player.current;
          if (!pl) return;
          try {
            if (userPaused.current && !pl.paused()) pl.pause();
            setTime(pl.time() || 0); setDuration(pl.duration() || 0); setMuted(pl.muted());
          } catch { /* not ready */ }
        }, 500);
      } catch {
        onFail?.();
      }
    })();
    return () => { cancelled = true; clearInterval(tick); player.current = null; el.innerHTML = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.kind, source?.video, source?.channel, source?.id]);

  if (source?.kind === "kick") {
    return <iframe className="vp-frame" src={`https://player.kick.com/${source.channel}?muted=true&autoplay=true`} title="Your stream" allow="autoplay; encrypted-media" />;
  }

  const toggle = () => {
    const pl = player.current;
    if (!pl) return;
    try {
      if (paused) { userPaused.current = false; pl.play(); }
      else { userPaused.current = true; pl.pause(); setPaused(true); }
    } catch { /* ignore */ }
  };
  const toggleMute = () => {
    const pl = player.current;
    if (!pl) return;
    try { pl.setMuted(!muted); setMuted(!muted); } catch { /* ignore */ }
  };
  const shown = scrub ?? time;
  const pct = duration ? (shown / duration) * 100 : 0;

  return (
    <>
      <div className="vp-host" ref={host} />
      {/* Catches every tap on the video so nothing opens Twitch / YouTube. */}
      <button type="button" className="vp-tap" onClick={toggle} aria-label={paused ? "Play" : "Pause"} />
      {/* Covers the platform's own oversized play button while it isn't playing. */}
      {paused && <span className="vp-cover" aria-hidden="true" />}
      {paused && <span className="vp-bigplay" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="7 4 20 12 7 20 7 4" /></svg></span>}
      <div className="vp-bar">
        <button type="button" className="vp-btn" onClick={toggle} aria-label={paused ? "Play" : "Pause"}>
          {paused
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="7 4 20 12 7 20 7 4" /></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>}
        </button>
        {duration > 0 && (
          <input
            type="range"
            className="vp-scrub"
            min={0}
            max={duration}
            step={1}
            value={shown}
            style={{ "--pct": `${pct}%` }}
            aria-label="Seek"
            onChange={(e) => setScrub(Number(e.target.value))}
            onPointerUp={() => { if (scrub != null) { player.current?.seek(scrub); setScrub(null); } }}
            onKeyUp={() => { if (scrub != null) { player.current?.seek(scrub); setScrub(null); } }}
          />
        )}
        <button type="button" className="vp-btn" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
          {muted
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 5.5a9 9 0 0 1 0 13" /></svg>}
        </button>
        <span className="vp-time">{duration > 0 ? `${fmt(shown)} / ${fmt(duration)}` : "LIVE"}</span>
      </div>
    </>
  );
}
